import React, { useEffect, useRef, useState, useCallback } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./editExperienceCenter.css";
import { updateExperienceCenter, viewExperienceCenter } from "../../Redux/Actions/experienceCenterActions";
import { useDispatch, useSelector } from "react-redux";
import {
    viewCityListPagination,
    viewCountryListPagination,
    viewStateListPagination,
} from "../../Redux/Actions/settingsActions";
import toast from "react-hot-toast";
import AddImage from "../../Global Functions/addImage";
import { GoogleMap, useLoadScript, Marker, Autocomplete } from "@react-google-maps/api";

/* ── Google Maps config ─────────────────────────────────────────────────── */
const MAP_LIBRARIES = ["places"];
const MAP_CONTAINER_STYLE = { width: "100%", height: "380px", borderRadius: "10px" };
const DEFAULT_CENTER = { lat: 28.6139, lng: 77.209 };

const EditExperienceCenter = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const coverInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const mapRef = useRef(null);
    const autocompleteRef = useRef(null);

    const { countryList } = useSelector((state) => state.country);
    const { states } = useSelector((state) => state.states);
    const cityState = useSelector((state) => state.city || {});
    const cities = Array.isArray(cityState?.citiesAllList?.data?.data)
        ? cityState.citiesAllList.data.data
        : [];
    const { loading = false } = useSelector((state) => state.experienceCenter || {});

    /* ── Google Maps load ───────────────────────────────────────────────── */
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        libraries: MAP_LIBRARIES,
    });

    /* ── UI state ───────────────────────────────────────────────────────── */
    const [activeSection, setActiveSection] = useState(0);
    const [errors, setErrors] = useState({});

    const [coverPreview, setCoverPreview] = useState(null);
    const [uploadingCover, setUploadingCover] = useState(false);
    const [coverDragOver, setCoverDragOver] = useState(false);

    const [videoPreview, setVideoPreview] = useState(null);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [videoDragOver, setVideoDragOver] = useState(false);

    const imageInputRef = useRef(null);
    const [images, setImages] = useState([]);
    const [uploadingImages, setUploadingImages] = useState(false);

    /* ── Map state ──────────────────────────────────────────────────────── */
    const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
    const [markerPosition, setMarkerPosition] = useState(null);

    /* ── Form data ──────────────────────────────────────────────────────── */
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        type: "",
        address: "",
        cover_image: "",
        video: "",
        image: [],
        country: "",
        state: "",
        city: "",
        location: { type: "Point", coordinates: [0, 0] },
        google_url: "",
        open_timing: "",
        close_timing: "",
    });

    const [stateData, setStateData] = useState({ country: "" });
    const [cityData, setCityData] = useState({ state: "", country: "", page: 1, limit: 1000 });

    /* ── Effects ────────────────────────────────────────────────────────── */
    useEffect(() => { dispatch(viewCountryListPagination()); }, [dispatch]);

    useEffect(() => {
        if (!id) return;
        const fetchCenter = async () => {
            try {
                const res = await dispatch(viewExperienceCenter(id));
                if (res?.data) {
                    const d = res.data;
                    const countryVal = d.country?._id || d.country || "";
                    const stateVal = d.state?._id || d.state || "";

                    // Hydrate location from existing data
                    const existingCoords =
                        Array.isArray(d.location?.coordinates) && d.location.coordinates.length === 2
                            ? d.location.coordinates          // [lng, lat]
                            : [0, 0];

                    setFormData({
                        title: d.title || "",
                        description: d.description || "",
                        type: d.type || "",
                        address: d.address || "",
                        cover_image: d.cover_image || "",
                        video: d.video || "",
                        country: countryVal,
                        state: stateVal,
                        city: d.city?._id || d.city || "",
                        location: { type: "Point", coordinates: existingCoords },
                        google_url: d.google_url || "",
                        open_timing: d.open_timing
                            ? new Date(d.open_timing).toISOString().slice(11, 16)
                            : "",
                        close_timing: d.close_timing
                            ? new Date(d.close_timing).toISOString().slice(11, 16)
                            : "",
                    });
                    if (d.image && Array.isArray(d.image) && d.image.length > 0) {
                        setImages(d.image);
                        setFormData((prev) => ({ ...prev, image: d.image }));
                    }
                    if (d.cover_image) setCoverPreview(d.cover_image);
                    if (d.video) setVideoPreview(d.video);

                    if (countryVal) {
                        setStateData({ country: countryVal });
                        setCityData((prev) => ({ ...prev, country: countryVal }));
                    }
                    if (stateVal) {
                        setCityData((prev) => ({ ...prev, state: stateVal }));
                    }

                    // Pre-position the map marker if coordinates exist
                    if (existingCoords[0] !== 0 || existingCoords[1] !== 0) {
                        const pos = { lat: existingCoords[1], lng: existingCoords[0] };
                        setMarkerPosition(pos);
                        setMapCenter(pos);
                    }
                }
            } catch {
                toast.error("Failed to load experience center details");
            }
        };
        fetchCenter();
    }, [id, dispatch]);

    useEffect(() => {
        const t = setTimeout(() => {
            if (stateData.country !== "") dispatch(viewStateListPagination(stateData));
        }, 300);
        return () => clearTimeout(t);
    }, [stateData.country, dispatch, stateData]);

    useEffect(() => {
        const t = setTimeout(() => {
            if (cityData.state !== "") dispatch(viewCityListPagination(cityData));
        }, 300);
        return () => clearTimeout(t);
    }, [cityData, cityData.state, dispatch]);

    /* ── Helpers ────────────────────────────────────────────────────────── */
    const clearError = (field) =>
        setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "country") {
            setStateData({ country: value });
            setCityData((prev) => ({ ...prev, country: value, state: "" }));
            setFormData((prev) => ({ ...prev, country: value, state: "", city: "" }));
            clearError("country");
            return;
        }
        if (name === "state") {
            setCityData((prev) => ({ ...prev, state: value }));
            setFormData((prev) => ({ ...prev, state: value, city: "" }));
            clearError("state");
            return;
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
        clearError(name);
    };

    /* ── Google Maps handlers ───────────────────────────────────────────── */
    const onMapLoad = useCallback((map) => { mapRef.current = map; }, []);

    const geocodeLatLng = useCallback((lat, lng) => {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results[0]) {
                setFormData((prev) => ({
                    ...prev,
                    address: prev.address || results[0].formatted_address,
                    location: { type: "Point", coordinates: [lng, lat] },
                    google_url: `https://maps.google.com/?q=${lat},${lng}`,
                }));
                clearError("location");
            }
        });
    }, []); // eslint-disable-line

    const onMapClick = useCallback((event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        setMarkerPosition({ lat, lng });
        setFormData((prev) => ({
            ...prev,
            location: { type: "Point", coordinates: [lng, lat] },
            google_url: `https://maps.google.com/?q=${lat},${lng}`,
        }));
        geocodeLatLng(lat, lng);
    }, [geocodeLatLng]);

    const onAutocompleteLoad = (ac) => { autocompleteRef.current = ac; };

    const onPlaceChanged = () => {
        if (!autocompleteRef.current) return;
        const place = autocompleteRef.current.getPlace();
        if (!place.geometry) return;

        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        setMarkerPosition({ lat, lng });
        setMapCenter({ lat, lng });

        setFormData((prev) => ({
            ...prev,
            address: place.formatted_address || prev.address,
            location: { type: "Point", coordinates: [lng, lat] },
            google_url: `https://maps.google.com/?q=${lat},${lng}`,
        }));
        clearError("location");
        clearError("address");
    };

    /* ── File upload helpers ────────────────────────────────────────────── */
    const processCoverFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) { toast.error("Please upload an image file"); return; }
        setCoverPreview(URL.createObjectURL(file));
        try {
            setUploadingCover(true);
            const url = await AddImage(file);
            setFormData((prev) => ({ ...prev, cover_image: url }));
            clearError("cover_image");
        } catch {
            toast.error("Cover image upload failed");
            setCoverPreview(null);
            setFormData((prev) => ({ ...prev, cover_image: "" }));
        } finally { setUploadingCover(false); }
    };

    const processVideoFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("video/")) { toast.error("Please upload a video file"); return; }
        setVideoPreview(URL.createObjectURL(file));
        try {
            setUploadingVideo(true);
            const url = await AddImage(file);
            setFormData((prev) => ({ ...prev, video: url }));
            clearError("video");
        } catch {
            toast.error("Video upload failed");
            setVideoPreview(null);
            setFormData((prev) => ({ ...prev, video: "" }));
        } finally { setUploadingVideo(false); }
    };

    const handleCoverFileChange = (e) => processCoverFile(e.target.files?.[0]);
    const handleCoverDrop = (e) => { e.preventDefault(); setCoverDragOver(false); processCoverFile(e.dataTransfer.files?.[0]); };
    const removeCover = () => { setCoverPreview(null); setFormData((prev) => ({ ...prev, cover_image: "" })); if (coverInputRef.current) coverInputRef.current.value = ""; };

    const handleVideoFileChange = (e) => processVideoFile(e.target.files?.[0]);
    const handleVideoDrop = (e) => { e.preventDefault(); setVideoDragOver(false); processVideoFile(e.dataTransfer.files?.[0]); };
    const removeVideo = () => { setVideoPreview(null); setFormData((prev) => ({ ...prev, video: "" })); if (videoInputRef.current) videoInputRef.current.value = ""; };

    const processImageFiles = async (files) => {
        if (!files || files.length === 0) return;
        const fileArray = Array.from(files);
        const remaining = 5 - images.length;
        if (remaining <= 0) {
            toast.error("Maximum 5 images allowed");
            return;
        }
        const toUpload = fileArray.slice(0, remaining);
        const invalidFiles = toUpload.filter((f) => !f.type.startsWith("image/"));
        if (invalidFiles.length > 0) {
            toast.error("Please upload image files only");
            return;
        }
        try {
            setUploadingImages(true);
            const uploadedUrls = await Promise.all(toUpload.map((file) => AddImage(file)));
            const newImages = [...images, ...uploadedUrls];
            setImages(newImages);
            setFormData((prev) => ({ ...prev, image: newImages }));
            clearError("image");
        } catch {
            toast.error("Image upload failed");
        } finally {
            setUploadingImages(false);
            if (imageInputRef.current) imageInputRef.current.value = "";
        }
    };

    const removeImage = (index) => {
        const newImages = images.filter((_, i) => i !== index);
        setImages(newImages);
        setFormData((prev) => ({ ...prev, image: newImages }));
    };

    const handleImageFileChange = (e) => processImageFiles(e.target.files);
    const handleImageDrop = (e) => { e.preventDefault(); processImageFiles(e.dataTransfer.files); };

    /* ── Section navigation ─────────────────────────────────────────────── */
    const sections = ["Basic Info", "Location & Address"];

    const handleNextSection = () => {
        if (activeSection === 0) {
            const sectionErrors = {};
            if (!formData.title.trim()) sectionErrors.title = "Title is required";
            if (!formData.type) sectionErrors.type = "Type is required";
            if (Object.keys(sectionErrors).length) { setErrors((prev) => ({ ...prev, ...sectionErrors })); return; }
        }
        setActiveSection((s) => Math.min(sections.length - 1, s + 1));
    };

    /* ── Validation ─────────────────────────────────────────────────────── */
    const validate = () => {
        const newErrors = {};
        if (!formData.title.trim()) newErrors.title = "Title is required";
        if (!formData.type) newErrors.type = "Type is required";
        if (!formData.address.trim()) newErrors.address = "Address is required";
        if (!formData.country) newErrors.country = "Country is required";
        if (!formData.state) newErrors.state = "State is required";
        if (!formData.city) newErrors.city = "City is required";
        if (!images || images.length < 1) newErrors.image = "Please upload at least 1 image";
        if (formData.open_timing && formData.close_timing) {
            if (formData.close_timing === formData.open_timing) {
                newErrors.close_timing = "Open and close time cannot be the same";
            }
        }
        const [lng, lat] = formData.location.coordinates;
        if (lng === 0 && lat === 0) newErrors.location = "Please select a location on the map";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /* ── Submit ─────────────────────────────────────────────────────────── */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            if (!formData.title.trim()) { setActiveSection(0); }
            else { setActiveSection(1); }
            return;
        }

        try {
            const isOvernight = formData.close_timing < formData.open_timing;
            const payload = {
                title: formData.title,
                description: formData.description,
                type: formData.type,
                address: formData.address,
                cover_image: formData.cover_image,
                video: formData.video,
                country: formData.country,
                image: images,
                state: formData.state,
                city: formData.city,
                location: formData.location,
                open_timing: formData.open_timing
                    ? new Date(`1970-01-01T${formData.open_timing}:00`)
                    : null,
                close_timing: formData.close_timing
                    ? new Date(`1970-01-0${isOvernight ? 2 : 1}T${formData.close_timing}:00`)
                    : null,
                id,
            };
            if (formData.google_url) payload.google_url = formData.google_url;

            const response = await dispatch(updateExperienceCenter(id, payload));
            if (response?.statusCode === 200) {
                toast.success("Experience center updated successfully!");
                navigate("/experience_center");
            }
        } catch (error) {
            const msg = error?.response?.data?.message || error?.message || "Failed to update experience center";
            if (msg.toLowerCase().includes("duplicate") || msg.toLowerCase().includes("already")) {
                setErrors((prev) => ({ ...prev, title: "Title already exists" }));
                setActiveSection(0);
                return toast.error("Title already exists");
            }
            toast.error(msg);
        }
    };

    /* ── Coordinate display helpers ─────────────────────────────────────── */
    const [lng, lat] = formData.location.coordinates;
    const hasLocation = !(lng === 0 && lat === 0);

    return (
        <MainLayout>
            <div className="eec-wrapper">
                {/* Header */}
                <div className="eec-header">
                    <div className="eec-header-left">
                        <div className="eec-header-icon">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                <polyline points="9 22 9 12 15 12 15 22" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="eec-title">Edit Experience Center</h1>
                            <p className="eec-subtitle">Update the details of this experience center</p>
                        </div>
                    </div>
                    <Link to="/experience_center" className="eec-view-btn">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                        </svg>
                        View Centers
                    </Link>
                </div>

                {/* Section Nav */}
                <div className="eec-nav">
                    {sections.map((sec, i) => (
                        <button key={i} type="button"
                            className={`eec-nav-pill ${activeSection === i ? "eec-nav-pill--active" : ""}`}
                            onClick={() => setActiveSection(i)}
                            disabled={loading || uploadingCover || uploadingVideo || uploadingImages}>
                            <span className="eec-nav-num">{String(i + 1).padStart(2, "0")}</span>
                            {sec}
                        </button>
                    ))}
                </div>

                <form className="eec-form" onSubmit={handleSubmit}>
                    {/* ── Section 1: Basic Info ── */}
                    <div className={`eec-section ${activeSection === 0 ? "eec-section--visible" : ""}`}>
                        <div className="eec-section-header">
                            <span className="eec-section-badge">01</span>
                            <h2 className="eec-section-title">Basic Information</h2>
                        </div>

                        <div className="eec-grid eec-grid-2">
                            <div className="eec-field eec-field--full">
                                <label className="eec-label">Title <span className="eec-req">*</span></label>
                                <input className={`eec-input ${errors.title ? "eec-input--error" : ""}`} type="text" name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Downtown Experience Center" disabled={loading} />
                                {errors.title && <span className="eec-error-msg">{errors.title}</span>}
                            </div>
                            <div className="eec-field eec-field--full">
                                <label className="eec-label">Type <span className="eec-req">*</span></label>
                                <select
                                    className={`eec-select ${errors.type ? "eec-input--error" : ""}`}
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    <option value="">Select type</option>
                                    <option value="available">Available</option>
                                    <option value="coming_soon">Coming Soon</option>
                                </select>
                                {errors.type && <span className="eec-error-msg">{errors.type}</span>}
                            </div>
                            <div className="eec-field eec-field--full">
                                <label className="eec-label">Description <span className="eec-optional">(Optional)</span></label>
                                <textarea className="eec-textarea" name="description" value={formData.description} onChange={handleChange} placeholder="Describe the experience center..." disabled={loading} rows={3} />
                            </div>

                            <div className="eec-field">
                                <label className="eec-label">Open Time</label>
                                <input
                                    type="time"
                                    name="open_timing"
                                    className="eec-input"
                                    value={formData.open_timing}
                                    onChange={handleChange}
                                    disabled={loading}
                                />
                            </div>

                            <div className="eec-field">
                                <label className="eec-label">Close Time</label>
                                <input
                                    type="time"
                                    name="close_timing"
                                    className="eec-input"
                                    value={formData.close_timing}
                                    onChange={handleChange}
                                    disabled={loading}
                                />
                            </div>

                            {/* Cover Image */}
                            <div className="eec-field eec-field--full">
                                <label className="eec-label">Cover Image <span className="eec-optional">(Optional)</span></label>
                                {coverPreview ? (
                                    <div className="eec-preview">
                                        <div className="eec-preview-img-wrap">
                                            {uploadingCover && (
                                                <div className="eec-uploading-overlay">
                                                    <svg className="eec-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                                    </svg>
                                                    <span>Uploading...</span>
                                                </div>
                                            )}
                                            <img src={coverPreview} alt="Cover preview" className="eec-preview-img" />
                                        </div>
                                        <div className="eec-preview-info">
                                            <span className="eec-preview-label">Cover image uploaded</span>
                                            {formData.cover_image && <span className="eec-preview-url">{formData.cover_image.slice(0, 48)}...</span>}
                                            <div className="eec-preview-actions">
                                                <button type="button" className="eec-change-btn" onClick={() => coverInputRef.current?.click()} disabled={uploadingCover || loading}>Change</button>
                                                <button type="button" className="eec-remove-btn" onClick={removeCover} disabled={uploadingCover || loading}>Remove</button>
                                            </div>
                                        </div>
                                        <input ref={coverInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleCoverFileChange} />
                                    </div>
                                ) : (
                                    <div className={`eec-dropzone ${coverDragOver ? "eec-dropzone--over" : ""}`} onClick={() => coverInputRef.current?.click()} onDragOver={(e) => { e.preventDefault(); setCoverDragOver(true); }} onDragLeave={() => setCoverDragOver(false)} onDrop={handleCoverDrop} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && coverInputRef.current?.click()}>
                                        <div className="eec-dropzone-icon">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                                <circle cx="8.5" cy="8.5" r="1.5" />
                                                <polyline points="21 15 16 10 5 21" />
                                            </svg>
                                        </div>
                                        <div className="eec-dropzone-text">
                                            <span className="eec-dropzone-title">{coverDragOver ? "Drop image here" : "Upload cover image"}</span>
                                            <span className="eec-dropzone-sub">Click to browse or drag & drop · PNG, JPG, WebP</span>
                                        </div>
                                        <input ref={coverInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleCoverFileChange} />
                                    </div>
                                )}
                            </div>

                            {/* Video */}
                            <div className="eec-field eec-field--full">
                                <label className="eec-label">Video <span className="eec-optional">(Optional)</span></label>
                                {videoPreview ? (
                                    <div className="eec-preview">
                                        <div className="eec-preview-video-wrap">
                                            {uploadingVideo && (
                                                <div className="eec-uploading-overlay">
                                                    <svg className="eec-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                                    </svg>
                                                    <span>Uploading...</span>
                                                </div>
                                            )}
                                            <video src={videoPreview} className="eec-preview-video" muted />
                                        </div>
                                        <div className="eec-preview-info">
                                            <span className="eec-preview-label">Video uploaded</span>
                                            {formData.video && <span className="eec-preview-url">{formData.video.slice(0, 48)}...</span>}
                                            <div className="eec-preview-actions">
                                                <button type="button" className="eec-change-btn" onClick={() => videoInputRef.current?.click()} disabled={uploadingVideo || loading}>Change</button>
                                                <button type="button" className="eec-remove-btn" onClick={removeVideo} disabled={uploadingVideo || loading}>Remove</button>
                                            </div>
                                        </div>
                                        <input ref={videoInputRef} type="file" accept="video/*" style={{ display: "none" }} onChange={handleVideoFileChange} />
                                    </div>
                                ) : (
                                    <div className={`eec-dropzone ${videoDragOver ? "eec-dropzone--over" : ""}`} onClick={() => videoInputRef.current?.click()} onDragOver={(e) => { e.preventDefault(); setVideoDragOver(true); }} onDragLeave={() => setVideoDragOver(false)} onDrop={handleVideoDrop} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && videoInputRef.current?.click()}>
                                        <div className="eec-dropzone-icon">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <polygon points="23 7 16 12 23 17 23 7" />
                                                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                                            </svg>
                                        </div>
                                        <div className="eec-dropzone-text">
                                            <span className="eec-dropzone-title">{videoDragOver ? "Drop video here" : "Upload video"}</span>
                                            <span className="eec-dropzone-sub">Click to browse or drag & drop · MP4, MOV, WebM</span>
                                        </div>
                                        <input ref={videoInputRef} type="file" accept="video/*" style={{ display: "none" }} onChange={handleVideoFileChange} />
                                    </div>
                                )}
                            </div>

                            <div className="eec-field eec-field--full">
                                <label className="eec-label">
                                    Images <span className="eec-req">*</span>
                                    <span className="eec-optional"> (Min 1, Max 5)</span>
                                </label>

                                {images.length > 0 && (
                                    <div className="eec-image-grid">
                                        {images.map((url, index) => (
                                            <div key={index} className="eec-image-thumb-wrap">
                                                <img src={url} alt={`uploaded-${index}`} className="eec-image-thumb" />
                                                <button
                                                    type="button"
                                                    className="eec-image-remove-btn"
                                                    onClick={() => removeImage(index)}
                                                    disabled={uploadingImages || loading}
                                                >
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                        <line x1="18" y1="6" x2="6" y2="18" />
                                                        <line x1="6" y1="6" x2="18" y2="18" />
                                                    </svg>
                                                </button>
                                            </div>
                                        ))}

                                        {images.length < 5 && (
                                            <div className="eec-image-add-btn" onClick={() => imageInputRef.current?.click()}>
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                    <line x1="12" y1="5" x2="12" y2="19" />
                                                    <line x1="5" y1="12" x2="19" y2="12" />
                                                </svg>
                                                <span>Add More</span>
                                                <input ref={imageInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleImageFileChange} />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {images.length === 0 && (
                                    <div
                                        className="eec-dropzone"
                                        onClick={() => imageInputRef.current?.click()}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={handleImageDrop}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === "Enter" && imageInputRef.current?.click()}
                                    >
                                        <div className="eec-dropzone-icon">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                                <circle cx="8.5" cy="8.5" r="1.5" />
                                                <polyline points="21 15 16 10 5 21" />
                                            </svg>
                                        </div>
                                        <div className="eec-dropzone-text">
                                            <span className="eec-dropzone-title">
                                                {uploadingImages ? "Uploading..." : "Upload images"}
                                            </span>
                                            <span className="eec-dropzone-sub">
                                                Click to browse or drag & drop · Min 1, Max 5 · PNG, JPG, WebP
                                            </span>
                                        </div>
                                        <input ref={imageInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleImageFileChange} />
                                    </div>
                                )}

                                {uploadingImages && <p className="eec-upload-progress">Uploading images, please wait...</p>}
                                {errors.image && <span className="eec-error-msg">{errors.image}</span>}
                            </div>
                        </div>
                    </div>

                    {/* ── Section 2: Location & Address ── */}
                    <div className={`eec-section ${activeSection === 1 ? "eec-section--visible" : ""}`}>
                        <div className="eec-section-header">
                            <span className="eec-section-badge">02</span>
                            <h2 className="eec-section-title">Location & Address Details</h2>
                        </div>

                        <div className="eec-grid eec-grid-2">
                            {/* Google Map */}
                            <div className="eec-field eec-field--full">
                                <label className="eec-label">
                                    Pin Location on Map <span className="eec-req">*</span>
                                </label>

                                {loadError && (
                                    <div className="eec-map-placeholder eec-map-placeholder--error">
                                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                                        <p>Map failed to load. Check your API key.</p>
                                    </div>
                                )}
                                {!isLoaded && !loadError && (
                                    <div className="eec-map-placeholder">
                                        <svg className="eec-spinner" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                        </svg>
                                        <p>Loading map…</p>
                                    </div>
                                )}

                                {isLoaded && !loadError && (
                                    <div className="eec-map-container">
                                        <Autocomplete onLoad={onAutocompleteLoad} onPlaceChanged={onPlaceChanged}>
                                            <div className="eec-map-search-wrap">
                                                <svg className="eec-map-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                                </svg>
                                                <input
                                                    type="text"
                                                    placeholder="Search to repin location…"
                                                    className="eec-map-search-input"
                                                />
                                            </div>
                                        </Autocomplete>

                                        <GoogleMap
                                            mapContainerStyle={MAP_CONTAINER_STYLE}
                                            center={mapCenter}
                                            zoom={markerPosition ? 14 : 13}
                                            onClick={onMapClick}
                                            onLoad={onMapLoad}
                                            options={{
                                                streetViewControl: false,
                                                mapTypeControl: false,
                                                fullscreenControl: false,
                                            }}
                                        >
                                            {markerPosition && <Marker position={markerPosition} />}
                                        </GoogleMap>

                                        <p className="eec-map-hint">
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                                            {markerPosition
                                                ? "Existing location loaded — click or search to repin"
                                                : "Search for a place above, or click on the map to pin location"}
                                        </p>
                                    </div>
                                )}

                                {errors.location && <span className="eec-error-msg">{errors.location}</span>}
                            </div>

                            {/* Coordinates (read-only) */}
                            <div className="eec-field">
                                <label className="eec-label">Longitude</label>
                                <input className="eec-input" type="text" value={hasLocation ? lng.toFixed(6) : "—"} readOnly disabled />
                            </div>
                            <div className="eec-field">
                                <label className="eec-label">Latitude</label>
                                <input className="eec-input" type="text" value={hasLocation ? lat.toFixed(6) : "—"} readOnly disabled />
                            </div>

                            {/* Location badge */}
                            {hasLocation && (
                                <div className="eec-field eec-field--full">
                                    <div className="eec-location-badge">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                                        Location pinned · {lat.toFixed(5)}, {lng.toFixed(5)}
                                        {formData.google_url && (
                                            <a href={formData.google_url} target="_blank" rel="noopener noreferrer" className="eec-gmaps-link">
                                                Open in Maps ↗
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Address */}
                            <div className="eec-field eec-field--full">
                                <label className="eec-label">Address <span className="eec-req">*</span></label>
                                <input
                                    className={`eec-input ${errors.address ? "eec-input--error" : ""}`}
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    placeholder="Street, building, area… (auto-filled from map)"
                                    disabled={loading}
                                />
                                {errors.address && <span className="eec-error-msg">{errors.address}</span>}
                            </div>

                            {/* Country */}
                            <div className="eec-field">
                                <label className="eec-label">Country <span className="eec-req">*</span></label>
                                <select className={`eec-select ${errors.country ? "eec-input--error" : ""}`} name="country" value={formData.country} onChange={handleChange} disabled={loading}>
                                    <option value="">Select country</option>
                                    {countryList?.data?.map((c) => (<option key={c._id} value={c._id}>{c.country_name}</option>))}
                                </select>
                                {errors.country && <span className="eec-error-msg">{errors.country}</span>}
                            </div>

                            {/* State */}
                            <div className="eec-field">
                                <label className="eec-label">State <span className="eec-req">*</span></label>
                                <select className={`eec-select ${errors.state ? "eec-input--error" : ""} ${!formData.country ? "eec-select--disabled" : ""}`} name="state" value={formData.state} onChange={handleChange} disabled={!formData.country || loading}>
                                    <option value="">{!formData.country ? "Select country first" : "Select state"}</option>
                                    {states?.data?.map((s) => (<option key={s._id} value={s._id}>{s.state_name}</option>))}
                                </select>
                                {errors.state && <span className="eec-error-msg">{errors.state}</span>}
                            </div>

                            {/* City */}
                            <div className="eec-field">
                                <label className="eec-label">City <span className="eec-req">*</span></label>
                                <select className={`eec-select ${errors.city ? "eec-input--error" : ""} ${!formData.state ? "eec-select--disabled" : ""}`} name="city" value={formData.city} onChange={handleChange} disabled={!formData.state || loading}>
                                    <option value="">{!formData.state ? "Select state first" : "Select city"}</option>
                                    {cities.map((c) => (<option key={c._id} value={c._id}>{c.city_name}</option>))}
                                </select>
                                {errors.city && <span className="eec-error-msg">{errors.city}</span>}
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="eec-footer">
                        <div className="eec-footer-nav">
                            <button type="button" className="eec-btn eec-btn--ghost" onClick={() => setActiveSection((s) => Math.max(0, s - 1))} disabled={activeSection === 0 || loading || uploadingCover || uploadingVideo}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                                Back
                            </button>
                            {activeSection < sections.length - 1 ? (
                                <button type="button" className="eec-btn eec-btn--primary" onClick={handleNextSection} disabled={loading || uploadingCover || uploadingVideo}>
                                    Next
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                                </button>
                            ) : (
                                <button type="button" onClick={handleSubmit} className="eec-btn eec-btn--submit" disabled={loading || uploadingCover || uploadingVideo}>
                                    {loading ? "Saving..." : (
                                        <>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                                            Update Experience Center
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                        <Link to="/experience_center" className="eec-btn eec-btn--cancel">Cancel</Link>
                    </div>
                </form>
            </div>
        </MainLayout>
    );
};

export default EditExperienceCenter;